import { useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, X } from 'lucide-react';
import { useDispatch } from 'react-redux';
import { Input } from '@/shared/widgets/input';
import { Button } from '@/shared/widgets/button';
import { usePopupHistory } from '@/shared/hooks/usePopupHistory';
import { searchBuild } from '../../config/search.build';
import { useSearch } from '../hooks/useSearch';
import type { AppDispatch } from '@/app/store/store';
import { SEARCH_PLACEHOLDER, trackSearchedForProduct } from '../../constants';
import SearchResults from './SearchResults';
import { openSearchModal, closeSearchModal } from '@/app/store/searchUiSlice';
import { useAnalytics } from '@/shared/features/analytics/presentation/hooks/useAnalytics';

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSearch?: (query: string) => void;
}

export default function SearchModal({ isOpen, onClose, onSearch }: SearchModalProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const dispatch = useDispatch<AppDispatch>();
  const { analytics } = useAnalytics();
  const navigate = useNavigate();
  const { service } = useMemo(() => searchBuild(dispatch), [dispatch]);
  const {
    searchQuery,
    setSearchQuery,
    setIsFocused,
    searchResults,
    isLoading,
    handleSuggestionClick: handleSuggestionClickFromHook,
  } = useSearch({
    service,
    onSearch,
  });

  const { goBack } = usePopupHistory({
    isOpen,
    onClose,
    stateKey: 'mobileSearchOpen',
  });

  // Auto-focus input and trigger search when modal opens
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      dispatch(openSearchModal());
      // Focus input after a small delay to ensure modal is rendered
      setTimeout(() => {
        inputRef.current?.focus();
        setIsFocused(true);
      }, 100);
    } else {
      document.body.style.overflow = '';
      dispatch(closeSearchModal());
      setIsFocused(false);
    }
    return () => {
      document.body.style.overflow = '';
      dispatch(closeSearchModal());
    };
  }, [isOpen, setIsFocused, dispatch]);

  // Submit current query (Enter key or programmatic) — don't call goBack() so navigation isn't cancelled
  const submitSearch = () => {
    // If exactly one product in results, navigate directly to its PDP
    const productList = searchResults?.products?.list;
    const trimmed = searchQuery.trim();
    if (productList?.length === 1 && productList[0].url) {
      trackSearchedForProduct(analytics, trimmed);
      const url = productList[0].url;
      if (/^https?:\/\//i.test(url)) {
        window.location.href = url;
      } else {
        navigate(url);
      }
      onClose();
      return;
    }
    if (trimmed && onSearch) {
      onSearch(trimmed);
    }
    onClose();
  };

  // Handle suggestion click — don't call goBack() so navigation to search page isn't cancelled
  const handleSuggestionClick = (suggestionText: string) => {
    void handleSuggestionClickFromHook(suggestionText);
    onClose();
  };

  // Handle trending item click – let AppLink drive SPA navigation; just close modal
  const handleTrendingItemClick = (_url: string) => {
    onClose();
  };

  // Handle product click – let AppLink drive SPA navigation; just close modal
  const handleProductItemClick = (_url: string) => {
    onClose();
  };

  const handleImageSearchClick = () => {
    analytics?.trackEvent('Image Search', { action: 'Clicked Image Search' });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-white flex flex-col">
      {/* Header */}
      <div className="flex items-center gap-3 p-4 border-b border-gray-200">
        <Button
          variant="ghost"
          size="icon"
          onClick={goBack}
          className="h-8 w-8 flex-shrink-0"
          aria-label="Back"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <Input
          ref={inputRef}
          type="text"
          placeholder={SEARCH_PLACEHOLDER}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              submitSearch();
            }
          }}
          className="flex-1 h-10"
        />
        <Button
          variant="ghost"
          size="icon"
          onClick={goBack}
          className="h-8 w-8 flex-shrink-0"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </Button>
      </div>

      {/* Content - Scrollable */}
      <div className="flex-1 overflow-y-auto px-4 pb-4">
        <div className="py-4">
          <SearchResults
            searchResults={searchResults}
            searchQuery={searchQuery}
            isLoading={isLoading}
            onSuggestionClick={handleSuggestionClick}
            onTrendingClick={handleTrendingItemClick}
            onProductClick={handleProductItemClick}
            onImageSearchClick={handleImageSearchClick}
            onImageSearchSuccess={onClose}
          />
        </div>
      </div>
    </div>
  );
}
